import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';

import { VersionConflictDialogComponent } from '../version-conflict-dialog/version-conflict-dialog.component';

/**
 * Opens the shared reload-and-reapply dialog for a `409` version conflict and
 * emits once the user acknowledges it.
 *
 * Every save path that can be rejected for a stale `version` recovers the same
 * way — acknowledge, then reload the current record before re-applying the edit
 * — so they all open the dialog through here instead of each repeating the
 * `open()` configuration. `disableClose` keeps the dialog to its one action:
 * there is nothing useful to do with a stale save other than start over from
 * the current record.
 *
 * Deciding *whether* a failure is a version conflict stays with the caller —
 * the admin APIs surface it as an {@link Error} carrying `code`, the vendor
 * APIs as a FAILURE envelope carrying `code`, and some endpoints return `409`
 * for reasons that have no version to conflict on (e.g. a duplicate email on
 * create), which must keep their own error message.
 *
 * The default body copy ("reload and re-apply your changes") only holds for
 * surfaces that reload the record in place. Callers that close on
 * acknowledgement — losing the in-progress edit — must pass a `message` that
 * says so instead of implying there's something to review below.
 */
export function openVersionConflictDialog(
  dialog: MatDialog,
  message?: string,
): Observable<boolean | undefined> {
  return dialog
    .open(VersionConflictDialogComponent, { width: '420px', disableClose: true, data: { message } })
    .afterClosed();
}

/**
 * Body copy for surfaces that close their edit dialog on acknowledgement
 * instead of reloading the record in place — there is nothing left to review
 * "below", so the message says the edit was dropped and the list refreshed
 * instead of implying the form is still open.
 */
export const VERSION_CONFLICT_DIALOG_CLOSED_MESSAGE =
  'This record was changed by someone else, so your unsaved changes were not applied. ' +
  'The list has been refreshed — please reopen it to make your change again.';
